'use memo'

import { AuthContext } from '@urbanx/agx.auth.client'
import { AgxFileUpload } from '@urbanx/agx-ui-components'
import {
  AgxButton,
  AgxModal,
  AgxRadioGroup,
  AgxSpacer,
  AgxTextInput,
  type RadioGroupOptions,
  Icon_AlertCircleOutline,
} from '@urbanx/agx.ui-components2'
import {
  ContractDocuments,
  DocumentTypes,
  EarlyReleaseOfDeposit,
  EarlyReleaseOfDepositDestinationOfTransfer,
  StaffUpdateContractEarlyReleaseRequest,
  UploadedDocument,
  useInvalidateUseStaffGetContractDeposits,
  useInvalidateUseStaffGetContractEarlyRelease,
  useStaffUpdateContractEarlyRelease,
} from '@urbanx/agx.campaigns.client'
import { useSelectedCampaign, useSelectedContract } from 'hooks'
import { useContext, useEffect, useState } from 'react'
import { deleteFile, getFileLink, uploadFile } from 'services'
import { parseNumber } from 'utils/parseNumber'

const destinationOptions: RadioGroupOptions[] = [
  { label: 'Vendor', value: EarlyReleaseOfDepositDestinationOfTransfer.Vendor },
  {
    label: "Vendor's Solicitor",
    value: EarlyReleaseOfDepositDestinationOfTransfer.VendorSolicitor,
  },
]

interface EarlyReleaseModalProps {
  isOpen: boolean
  onClose: () => void
}

const buildRenamedFormData = (contractId: string, file: File) => {
  const renamedFile = new File([file], `${contractId}/${file.name}`, {
    type: file.type,
    lastModified: file.lastModified,
  })
  const renamedFormData = new FormData()
  renamedFormData.append('File', renamedFile)
  return renamedFormData
}

const EarlyReleaseModal = ({
  isOpen,
  onClose,
}: EarlyReleaseModalProps) => {
  const { getAuthToken } = useContext(AuthContext)
  const { campaign } = useSelectedCampaign()
  const { contract } = useSelectedContract()
  const invalidateContractDeposits = useInvalidateUseStaffGetContractDeposits()
  const invalidateContractEarlyRelease =
    useInvalidateUseStaffGetContractEarlyRelease()

  const [
    buyerSolicitorEarlyDepositFiles,
    setBuyerSolicitorEarlyDepositFiles,
  ] = useState<(UploadedDocument | File)[]>([])
  const [
    vendorSolicitorEarlyDepositFiles,
    setVendorSolicitorEarlyDepositFiles,
  ] = useState<(UploadedDocument | File)[]>([])
  const [amount, setAmount] = useState<number | undefined>()
  const [destinationOfTransfer, setDestinationOfTransfer] = useState<
    EarlyReleaseOfDepositDestinationOfTransfer | undefined
  >()
  const [showValidation, setShowValidation] = useState(false)

  const {
    staffUpdateContractEarlyRelease,
    staffUpdateContractEarlyReleasePending,
  } = useStaffUpdateContractEarlyRelease({
      onSuccess: () => {},
      onError: () => {},
    })

  const canSubmit =
      buyerSolicitorEarlyDepositFiles.length > 0 &&
      vendorSolicitorEarlyDepositFiles.length > 0 &&
      amount != null &&
      amount > 0 &&
      destinationOfTransfer != null &&
      !staffUpdateContractEarlyReleasePending

  useEffect(() => {
    if (isOpen) return

    setBuyerSolicitorEarlyDepositFiles([])
    setVendorSolicitorEarlyDepositFiles([])
    setAmount(undefined)
    setDestinationOfTransfer(undefined)
    setShowValidation(false)
  }, [isOpen])

  const uploadSelectedFile = async (
    authToken: string,
    documentType: DocumentTypes,
    selectedFile?: UploadedDocument | File
  ) => {
    if (!campaign?.campaignId || !contract?.contractId || !selectedFile) {
      return undefined
    }

    if (!(selectedFile instanceof File)) {
      return selectedFile
    }

    const formData = buildRenamedFormData(contract.contractId, selectedFile)

    return uploadFile(
      authToken,
      formData,
      campaign.campaignId,
      documentType,
      null,
      null,
      undefined,
      contract.contractId
    )
  }

  const handleConfirm = async () => {
    setShowValidation(true)

    const campaignId = campaign?.campaignId
    const contractId = contract?.contractId

    if (!canSubmit || !campaignId || !contractId) {
      return
    }

    const authToken = await getAuthToken()
    if (!authToken) return

    const [buyerSolicitorConfirmation, vendorSolicitorConfirmation] =
      await Promise.all([
        uploadSelectedFile(
          authToken,
          DocumentTypes.BuyerSolicitorEarlyDeposit,
          buyerSolicitorEarlyDepositFiles[0]
        ),
        uploadSelectedFile(
          authToken,
          DocumentTypes.VendorSolicitorEarlyDeposit,
          vendorSolicitorEarlyDepositFiles[0]
        ),
      ])

    if (!buyerSolicitorConfirmation || !vendorSolicitorConfirmation) {
      return
    }

    const earlyReleaseOfDeposit: EarlyReleaseOfDeposit = {
      amount,
      destinationOfTransfer,
    }

    const contractDocuments: ContractDocuments = {
      buyerSolicitorConfirmation,
      vendorSolicitorConfirmation,
    }

    const request: StaffUpdateContractEarlyReleaseRequest = {
      earlyReleaseOfDeposit,
      contractDocuments,
    }

    await staffUpdateContractEarlyRelease(
      {
        campaignId,
        body: request,
        contractId,
      },
      {
        onSuccess: async (data) => {
          if (!data?.success) return

          await Promise.all([
            invalidateContractEarlyRelease(campaignId, contractId),
            invalidateContractDeposits(campaignId, contractId),
          ])
          onClose()
        },
      }
    )
  }

  return (
    <AgxModal
      title={'Early Release of Deposit'}
      titleIcon={
        <div className="modelTitleIconContainer">
          <Icon_AlertCircleOutline type={'Danger'} height={40} width={40} />
        </div>
      }
      type={'single'}
      variant={'withDivider'}
      actionButtonContainerStyle={'withDivider'}
      actionButtons={
        <AgxSpacer
          direction="horizontal"
          gap={'spacing-xl'}
          contentJustification="center"
          fill
        >
          <AgxButton
            id={'btnCancelEarlyRelease'}
            text={'Cancel'}
            variant={'hollow'}
            onClick={onClose}
            stretch
          />
          <AgxButton
            id={'btnConfirmEarlyRelease'}
            text={'Confirm'}
            variant={'primary'}
            danger
            disabled={!canSubmit}
            onClick={handleConfirm}
            stretch
          />
        </AgxSpacer>
      }
      content={
        <AgxSpacer
          direction="vertical"
          gap={'md'}
          fill
          padding={{ top: 'md', bottom: 'md', left: 'xl', right: 'xl' }}
        >
          <AgxFileUpload
            title="Buyer Solicitor Confirmation"
            id="fileBuyerSolicitorEarlyDeposit"
            documentType={DocumentTypes.BuyerSolicitorEarlyDeposit}
            buttonText={'Choose file'}
            fileData={buyerSolicitorEarlyDepositFiles}
            onValueChanged={({ value }) =>
              setBuyerSolicitorEarlyDepositFiles(value ?? [])
            }
            getAuthToken={getAuthToken}
            campaignId={campaign?.campaignId || ''}
            uploadFile={uploadFile}
            deleteFile={deleteFile}
            getFileLink={getFileLink}
            extensions={['pdf']}
            multiple={false}
            autoUpload={false}
            required
            validate={showValidation}
          />
          <AgxFileUpload
            title="Vendor Solicitor Confirmation"
            id="fileVendorSolicitorEarlyDeposit"
            documentType={DocumentTypes.VendorSolicitorEarlyDeposit}
            buttonText={'Choose file'}
            fileData={vendorSolicitorEarlyDepositFiles}
            onValueChanged={({ value }) =>
              setVendorSolicitorEarlyDepositFiles(value ?? [])
            }
            getAuthToken={getAuthToken}
            campaignId={campaign?.campaignId || ''}
            uploadFile={uploadFile}
            deleteFile={deleteFile}
            getFileLink={getFileLink}
            extensions={['pdf']}
            multiple={false}
            autoUpload={false}
            required
            validate={showValidation}
          />
          <AgxTextInput
            id="earlyReleaseAmount"
            label="Amount to be released"
            type="currency"
            stretch
            required
            hideOptionalText
            defaultValue={amount?.toString() ?? ''}
            onChange={(value) => setAmount(parseNumber(value))}
            errorText={
              showValidation && (amount == null || amount <= 0)
                ? 'Amount is required'
                : ''
            }
          />
          <AgxRadioGroup
            id="destinationOfTransfer"
            label="Destination of transfer"
            layout="vertical"
            options={destinationOptions}
            value={destinationOfTransfer}
            onChange={(value) =>
              setDestinationOfTransfer(
                value as EarlyReleaseOfDepositDestinationOfTransfer
              )
            }
            required
            validate={showValidation}
            errorText={
              showValidation && !destinationOfTransfer
                ? 'Select destination of transfer'
                : ''
            }
          />
        </AgxSpacer>
      }
      isOpen={isOpen}
      setIsDialogOpen={(nextValue) => {
        if (!nextValue) onClose()
      }}
    />
  )
}

export default EarlyReleaseModal
